import { FlexColumn, FlexContainer } from '@/styles/Containers';
import { modalStyles } from '@/styles/Modals';
import { CustomizableHeading, Letter } from '@/styles/Text';
import React, { useState } from 'react';
import Modal from 'react-modal';
import styled from 'styled-components';
import { Circle } from './CalculatorCourse';
import CloseIcon from '../../assets/icons/clear.svg';
import { CalculatorCourse } from './SemesterCalculator';
import { gradeValues } from './CalculatorTypes';

const ErrorMessage = styled.div`
  color: red;
  margin-top: 0.3rem;
  font-size: 0.75rem;
`;

const AddCourseButton = styled.button`
  background-color: ${({ theme }) => theme.color.darkBlue};
  color: ${({ theme }) => theme.color.white};
  border: none;
  transition: transform 0.3s ease-in-out;
  :hover {
    cursor: pointer;
    transform: scale(1.05);
  }
`;

const CloseModalButton = styled.button`
  align-self: flex-end;
  margin-left: 10rem;
  background: transparent;
  border: none;
`;

const StyledFlexContainer = styled(FlexContainer)`
  gap: 1rem;
  margin-bottom: 1rem;
`;

const StyledLetter = styled(Letter)<{ isSelected: boolean }>`
  color: ${({ isSelected }) => (isSelected ? 'white' : 'black')};
`;

const StyledCircle = styled(Circle)<{ isSelected: boolean }>`
  background-color: ${({ isSelected }) => (isSelected ? '#1E3859' : 'white')};
  &:hover {
    background-color: ${({ theme }) => theme.color.darkBlue};

    ${StyledLetter} {
      color: white;
    }
  }
`;

type CourseModalProps = {
  isOpen: boolean;
  handleCloseModal: (value: boolean) => void;
  addCourseToSemester: (newCourse: CalculatorCourse) => void;
};

const AddCourseModal = ({ isOpen, handleCloseModal, addCourseToSemester }: CourseModalProps) => {
  const toggleModalIsOpen = () => {
    handleCloseModal(false);
  };

  const [selectedGrade, setSelectedGrade] = useState('');
  const [courseName, setCourseName] = useState('');
  const [courseCode, setCourseCode] = useState('');
  const [grade, setGrade] = useState(-2);
  const [coursePoint, setCoursePoint] = useState(0);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({
    courseName: '',
    courseCode: '',
    coursePoint: '',
  });
  const [coursePointInput, setCoursePointInput] = useState('');
  const grades: string[] = ['A', 'B', 'C', 'D', 'E', 'F', 'Bestått'];

  const validateForm = () => {
    const num = parseFloat(coursePointInput);
    const newErrors = {
      courseName: courseName ? '' : 'Emnenavn påkrevd',
      courseCode: courseCode ? '' : 'Emnekode påkrevd',
      coursePoint: coursePointInput.trim() === '' ? 'Studiepoeng påkrevd' : isNaN(num) || num < 0 || num > 99.9 ? 'Studiepoeng må være et tall' : '',
    };
    setErrors(newErrors);

    const isValid = !Object.values(newErrors).some((error) => error !== '');
    setIsFormValid(isValid);
    if (isValid) {
      setCoursePoint(num);
    }
    return isValid;
  };

  const toggleRejectCloseModal = () => {
    handleCloseModal(false);
    if (isSubmitted) {
      setIsSubmitted(false);
    }
  };

  const addCourse = () => {
    setIsSubmitted(true);
    if (!validateForm()) {
      return;
    }
    const parsedCoursePoint = parseFloat(coursePointInput);
    const newCourse: CalculatorCourse = {
      courseCode: courseCode,
      courseName: courseName,
      coursePoint: parsedCoursePoint,
      grade: grade,
    };
    addCourseToSemester(newCourse);
    setIsSubmitted(true);
    toggleRejectCloseModal();
    setCourseCode('');
    setCourseName('');
    setCoursePoint(0);
    setCoursePointInput('');
    setGrade(-2);
    setSelectedGrade('');
  };

  const handleSetCourseName = (courseName: string) => {
    let displayCourseName = courseName.charAt(0).toUpperCase() + courseName.slice(1);
    if (courseName.length > 20) {
      const firstPart = courseName.split(' ')[0];
      displayCourseName = `${firstPart}, (...)`;
    }
    setCourseName(displayCourseName);
  };

  const handleSetCourseCode = (courseCode: string) => {
    const fixedCourseCode = courseCode.toUpperCase();
    setCourseCode(fixedCourseCode);
  };
  return (
    <Modal
      style={modalStyles}
      isOpen={isOpen}
      onRequestClose={toggleModalIsOpen}
      shouldCloseOnOverlayClick={false}
      appElement={document.getElementById('root') as HTMLElement}
    >
      <FlexColumn>
        <CloseModalButton onClick={toggleRejectCloseModal}>
          <img src={CloseIcon} alt='Close' />
        </CloseModalButton>
        <CustomizableHeading headingLevel='h2'>Legg til emne</CustomizableHeading>
        <Letter>Emnekode:</Letter>
        <input
          type='text'
          onChange={(e) => {
            handleSetCourseCode(e.target.value);
          }}
          maxLength={12}
        ></input>
        {isSubmitted && errors.courseCode && <ErrorMessage>{errors.courseCode}</ErrorMessage>}
        <Letter>Emnenavn:</Letter>
        <input
          type='text'
          onChange={(e) => {
            handleSetCourseName(e.target.value);
          }}
        ></input>
        {isSubmitted && errors.courseName && <ErrorMessage>{errors.courseName}</ErrorMessage>}
        <Letter>Studiepoeng:</Letter>
        <input
          type='text'
          value={coursePointInput}
          onChange={(e) => {
            let inputValue = e.target.value;
            inputValue = inputValue.replace(',', '.');
            setCoursePointInput(inputValue);
          }}
          maxLength={4}
        />

        {isSubmitted && errors.coursePoint && <ErrorMessage>{errors.coursePoint}</ErrorMessage>}
        <Letter>Karakter:</Letter>
        <StyledFlexContainer>
          {grades.slice(0, 6).map((gradeValue) => (
            <StyledCircle
              key={gradeValue}
              onClick={() => {
                setSelectedGrade(gradeValue);
                setGrade(gradeValues[gradeValue]);
              }}
              isSelected={selectedGrade === gradeValue}
            >
              <StyledLetter isSelected={selectedGrade === gradeValue}>{gradeValue}</StyledLetter>
            </StyledCircle>
          ))}
        </StyledFlexContainer>
        <AddCourseButton onClick={addCourse}>Legg til emne</AddCourseButton>
      </FlexColumn>
    </Modal>
  );
};
export default AddCourseModal;
